// @flow

/**
 * Module dependencies.
 */

import { Col, Container, Row } from 'client/components/core/grid';
import { Separator, separatorSizes } from 'client/components/separator';
import { branch } from 'core/utils/branch';
import { colors, media, units } from 'client/styles';
import { compose, defaultProps } from 'recompose';
import { ifProp, prop } from 'styled-tools';
import { position } from 'polished';
import BackgroundImage from 'client/components/core/images/background-image';
import Layout from 'client/components/core/layout';
import React from 'react';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  background: string,
  backgroundImage?: string,
  children?: React$Element<*>,
  paddingVertical: number,
  pullRight: boolean,
  pullTop: boolean,
  roundedBorder: boolean,
  separator: boolean,
  styles: string
};

/**
 * Set rounded border style.
 */

const setRoundedBorderStyle = (roundedBorder: boolean) => branch(roundedBorder, `
    border-radius: 2px;
  `, '');

/**
 * `Background` component.
 */

const Background = styled.div`
  ${position('absolute', '0', '0', '0', '0')}

  background-color: ${prop('color')};
  border-radius: ${ifProp('roundedBorder', ' 2px', 0)};
`;

/**
 * `ColAbsolute` component.
 */

const ColAbsolute = styled(Col)`
  ${position('absolute', '0', null, '0', '0')}
`;

/**
 * `VericalSeparator` component.
 */

const VericalSeparator = styled(Separator.Vertical)`
  top: -${separatorSizes.large / 2}px;
`;

/**
 * `ContentWrapper` component.
 */

const ContentWrapper = styled(Layout)`
  position: relative;
  z-index: 1;

  ${media.min.sm`
    margin-top: ${ifProp('pullTop', '-25vh', 0)};
    min-height: ${ifProp('pullTop', '25vh', 0)};
  `}

  ${prop('styles', '')}
`;

/**
 * Render `Separator` component.
 */

const renderSeparator = (condition: boolean) => {
  return branch(condition,
    <ColAbsolute
      xs={11}
      xsOffset={1}
    >
      <VericalSeparator absolute />
    </ColAbsolute>
  );
};

/**
 * Export `Content`.
 */

function Content(props: Props): React$Element<*> {
  const {
    background,
    backgroundImage,
    children,
    paddingVertical,
    pullRight,
    pullTop,
    roundedBorder,
    separator,
    styles
  } = props;

  const mdOffset = pullRight ? 1 : 0;

  return (
    <Row>
      <ContentWrapper
        paddingVertical={units(paddingVertical)}
        pullTop={pullTop}
        styles={styles}
      >
        <ColAbsolute
          md={11}
          mdOffset={mdOffset}
          xs={12}
        >
          <Background
            color={background}
            roundedBorder={roundedBorder}
          />

          {branch(backgroundImage,
            <BackgroundImage
              filename={backgroundImage}
              styles={setRoundedBorderStyle(roundedBorder)}
            />
          )}
        </ColAbsolute>

        {renderSeparator(separator)}

        <Container>
          {children}
        </Container>
      </ContentWrapper>
    </Row>
  );
}

export default compose(
  defaultProps({
    background: colors.white,
    paddingVertical: 10,
    pullRight: false,
    pullTop: false,
    roundedBorder: false,
    separator: false,
    styles: ''
  })
)(Content);



// WEBPACK FOOTER //
// ../src/client/components/content/index.js